import { inject, Injectable } from '@angular/core';
import { BroadcastChannelMessageTypes } from '../enums/BroadcastChannelMessageTypes';
import { CartStore } from '../../features/cart/core/stores/CartStore';
import { ICart } from '../../features/cart/core/interfaces/ICart';
import { ICartAction } from '../../features/cart/core/interfaces/ICartAction';
import { Router } from '@angular/router';
import { TerminalsStore } from '../../features/terminals/core/stores/TerminalsStore';
import { BroadcastChannelMessage } from '../types';
import { CustomerScreenService } from './CustomerScreenService';
import { CustomerScreenStore } from '../stores/CustomerScreenStore';
import { ReservationsStore } from '../../features/reservations/core/stores/ReservationsStore';
import { ISelectedPosition } from '../../features/cart/core/interfaces/ISelectedPosition';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BroadcastChannelService {

  private readonly _cashChannel = new BroadcastChannel('cash_channel');
  private readonly _cartStore = inject(CartStore);
  private readonly _router = inject(Router);
  private readonly _terminalsStore = inject(TerminalsStore);
  private readonly _customerScreenService = inject(CustomerScreenService);
  private readonly _customerScreenStore = inject(CustomerScreenStore);
  private readonly _reservationStore = inject(ReservationsStore);

  public readonly paySeparatelySelectionChanged$ = new Subject<{
    selectedPositions: ISelectedPosition[];
    isPaySeparatelyModeEnabled: boolean;
  }>();

  public startCashChannel(): void {
    this._cashChannel.addEventListener(
      'message',
      (res: BroadcastChannelMessage) => {
        if (res.data.type === BroadcastChannelMessageTypes.CART_CHANGES) {
          this.handleCartChanges(res);
        } else if (res.data.type === BroadcastChannelMessageTypes.SHOW_CHANGES) {
          this.handleShowChanges(res);
        } else if (res.data.type === BroadcastChannelMessageTypes.PING) {
          this.handlePing();
        } else if (res.data.type === BroadcastChannelMessageTypes.PONG) {
          this.handlePong();
        } else if (res.data.type === BroadcastChannelMessageTypes.BLANK_SCREEN) {
          this.handleBlankScreen();
        } else if (res.data.type === BroadcastChannelMessageTypes.DISABLE_CUSTOMER_SCREEN) {
          this.handleDisableCustomerScreen();
        } else if (res.data.type === BroadcastChannelMessageTypes.ENABLE_CUSTOMER_SCREEN) {
          this.handleEnableCustomerScreen();
        } else if (res.data.type === BroadcastChannelMessageTypes.PAY_SEPARATELY_SELECTION_CHANGED) {
          this.handlePaySeparatelySelectionChanged(res);
        } else if (res.data.type === BroadcastChannelMessageTypes.REBOOK_RESERVATION_CHANGED) {
          this.handleReebokReservation(res);
        }
      }
    );
  }

  public cashChannelPostMessage(message: { type: BroadcastChannelMessageTypes; data: any; }): void {
    this._cashChannel.postMessage(message);
  }

  private handleDisableCustomerScreen(): void {
    this._customerScreenStore.isCustomerScreenDisabled.set(true);
  }

  private handleEnableCustomerScreen(): void {
    this._customerScreenStore.isCustomerScreenDisabled.set(false);
  }

  private handleCartChanges(res: BroadcastChannelMessage): void {
    const { cart, cartAction } = res.data.data as { cart: ICart; cartAction: ICartAction; };

    if (cartAction.actionType === 'createCartAfterTransaction' || cartAction.actionType === 'createCart') {
      this._terminalsStore.selectedTerminal.update(terminal => ({
        ...terminal,
        currentSession: {
          ...terminal.currentSession,
          currentBasketId: cart.id
        }
      }));
    }

    this._cartStore.cart.set(cart);
    this._cartStore.cartAction$.next(cartAction);
  }

  private handleShowChanges(res: BroadcastChannelMessage): void {
    const { showId } = res.data.data as { showId: number; };
    this._router.navigateByUrl(`/shows/${showId}?isCustomerScreen=true`.langUrl());
  }

  private handleBlankScreen(): void {
    this._router.navigateByUrl('/blank-screen?isCustomerScreen=true'.langUrl());
  }

  private handlePing(): void {
    this._customerScreenService.lastPingTime$.next(Date.now());
  }

  private handlePong(): void {
    this._customerScreenService.lastPongTime$.next(Date.now());
  }

  private handlePaySeparatelySelectionChanged(res: BroadcastChannelMessage): void {
    const data = res.data.data as {
      selectedPositions: ISelectedPosition[];
      isPaySeparatelyModeEnabled: boolean;
    };
    this.paySeparatelySelectionChanged$.next(data);
  }

  private handleReebokReservation(res: BroadcastChannelMessage): void {
    this._reservationStore.rebookReservation.set(res.data.data);
  }
}
